/**
 * Styled XLSX for the per-booking export, written with exceljs (the xlsx
 * package cannot style cells). Two tabs:
 *
 * - Booking lines: each booking's supplier lines, a shaded Total row, then a
 *   grey separator row before the next booking. Lines are grouped under their
 *   Total row so Excel's outline buttons collapse the sheet to totals only.
 * - Bookings: one row per booking with the margin figures, for pivoting.
 */

import ExcelJS from "exceljs"

import { LINE_COLUMNS, SUMMARY_COLUMNS, buildBookingGroups, buildBookingSummaryRows } from "./booking-lines"

const COLOURS = {
	headerFill: "FF1F2A44",
	headerText: "FFFFFFFF",
	totalFill: "FFE7EBF1",
	separatorFill: "FFBFBFBF",
	lineBorder: "FFE3E3E3",
	totalBorder: "FF8A94A6",
	notConfirmedText: "FFC0392B",
	checkFill: "FFFCE4C4",
}

const NUMBER_FORMATS = {
	date: "dd/mm/yyyy",
	number: "0",
	money: "#,##0.00",
	rate: "0.0000",
	percent: "0.0%",
}

const solidFill = (argb) => ({ type: "pattern", pattern: "solid", fgColor: { argb } })

/**
 * Column widths, keys and number formats, plus a styled, frozen, filterable
 * header row.
 */
const setUpSheet = (sheet, columns, frozenColumns) => {
	sheet.columns = columns.map((column) => ({
		key: column.key,
		width: column.width,
		style: NUMBER_FORMATS[column.format] ? { numFmt: NUMBER_FORMATS[column.format] } : {},
	}))

	const header = sheet.getRow(1)
	columns.forEach((column, index) => {
		const cell = header.getCell(index + 1)
		cell.value = column.header
		cell.font = { bold: true, color: { argb: COLOURS.headerText } }
		cell.fill = solidFill(COLOURS.headerFill)
		cell.alignment = { vertical: "middle", wrapText: true }
	})
	header.height = 30

	sheet.views = [{ state: "frozen", xSplit: frozenColumns, ySplit: 1 }]
	sheet.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: columns.length } }
}

/**
 * Write one row object, leaving blanks ("", null, undefined) as empty cells.
 */
const addValues = (sheet, columns, values) => {
	const row = sheet.addRow(Object.fromEntries(columns.map((column) => [column.key, values[column.key] === "" || values[column.key] === undefined ? null : values[column.key]])))
	return row
}

const columnNumber = (columns, key) => columns.findIndex((column) => column.key === key) + 1

/**
 * Red text on "No" in the confirmed column and an amber cell when the lines
 * do not match the stored margins.
 */
const highlight = (row, columns) => {
	const confirmedIndex = columnNumber(columns, "supplierConfirmed")
	if (confirmedIndex > 0) {
		const confirmed = row.getCell(confirmedIndex)
		if (confirmed.value === "No") confirmed.font = { ...confirmed.font, bold: true, color: { argb: COLOURS.notConfirmedText } }
	}

	const checkIndex = columnNumber(columns, "check")
	if (checkIndex > 0) {
		const check = row.getCell(checkIndex)
		if (check.value && check.value !== "OK") check.fill = solidFill(COLOURS.checkFill)
	}
}

const styleRow = (row, count, { fill, font, border }) => {
	for (let index = 1; index <= count; index += 1) {
		const cell = row.getCell(index)
		if (fill) cell.fill = solidFill(fill)
		if (font) cell.font = font
		if (border) cell.border = border
	}
}

const writeLinesSheet = (workbook, groups) => {
	const sheet = workbook.addWorksheet("Booking lines", { properties: { outlineProperties: { summaryBelow: true } } })
	setUpSheet(sheet, LINE_COLUMNS, 2)
	const count = LINE_COLUMNS.length

	groups.forEach((group, index) => {
		group.lines.forEach((line) => {
			const row = addValues(sheet, LINE_COLUMNS, line)
			row.outlineLevel = 1
			styleRow(row, count, { border: { bottom: { style: "thin", color: { argb: COLOURS.lineBorder } } } })
			highlight(row, LINE_COLUMNS)
		})

		const total = addValues(sheet, LINE_COLUMNS, group.total)
		styleRow(total, count, {
			fill: COLOURS.totalFill,
			font: { bold: true },
			border: { top: { style: "thin", color: { argb: COLOURS.totalBorder } }, bottom: { style: "thin", color: { argb: COLOURS.totalBorder } } },
		})
		highlight(total, LINE_COLUMNS)

		if (index < groups.length - 1) {
			const separator = sheet.addRow([])
			separator.height = 6
			styleRow(separator, count, { fill: COLOURS.separatorFill })
		}
	})

	return sheet
}

const writeSummarySheet = (workbook, groups) => {
	const sheet = workbook.addWorksheet("Bookings")
	setUpSheet(sheet, SUMMARY_COLUMNS, 1)

	buildBookingSummaryRows(groups).forEach((summary) => {
		const row = addValues(sheet, SUMMARY_COLUMNS, summary)
		styleRow(row, SUMMARY_COLUMNS.length, { border: { bottom: { style: "thin", color: { argb: COLOURS.lineBorder } } } })
		highlight(row, SUMMARY_COLUMNS)
	})

	return sheet
}

/**
 * The full per-booking workbook for an AuditResult.
 *
 * @param {import("./types").AuditResult} result
 * @returns {ExcelJS.Workbook}
 */
export const buildBookingWorkbook = (result) => {
	const workbook = new ExcelJS.Workbook()
	workbook.created = new Date(result.generatedMillis)

	const groups = buildBookingGroups(result)
	writeLinesSheet(workbook, groups)
	writeSummarySheet(workbook, groups)

	return workbook
}

/**
 * Build the workbook and trigger a browser download.
 *
 * @param {import("./types").AuditResult} result
 * @param {string} fileName Without the .xlsx extension
 */
export const downloadBookingWorkbook = async (result, fileName) => {
	const buffer = await buildBookingWorkbook(result).xlsx.writeBuffer()
	const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
	const url = URL.createObjectURL(blob)

	const link = document.createElement("a")
	link.href = url
	link.download = `${fileName}.xlsx`
	document.body.appendChild(link)
	link.click()
	link.remove()
	URL.revokeObjectURL(url)
}
